import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Calendar, Check, X } from "lucide-react";
import { refreshCalendarTokensIfNeeded } from "@/utils/calendarTokenRefresh";

const CalendarCallback = () => {
  const { currentUser, session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  const processedCode = useRef<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // Prevent multiple processing attempts with the same code
      if (hasProcessed.current || (code && processedCode.current === code)) {
        console.log("OAuth callback already processed for this code, skipping...");
        return;
      }

      if (error || !code) {
        console.error('OAuth error:', error || 'No authorization code received');
        setStatus('error');
        setErrorMessage(error || 'No authorization code received');
        return;
      }

      if (!currentUser || !session) {
        console.error('No authenticated user found');
        setStatus('error');
        setErrorMessage('Authentication required. Please sign in and try again.');
        
        sessionStorage.setItem('calendarCallbackUrl', window.location.href);
        
        setTimeout(() => {
          navigate('/');
        }, 3000);
        return;
      }

      // Mark as processed to prevent duplicate attempts
      hasProcessed.current = true;
      processedCode.current = code;

      // Clear the URL parameters immediately to prevent reprocessing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      try {
        console.log("Processing callback with code:", code.substring(0, 10) + '...');
        
        // Parse state to determine provider
        let provider = 'google'; // default
        try {
          if (state) {
            const stateData = JSON.parse(atob(state));
            provider = stateData.provider || 'google';
          }
        } catch (e) {
          console.warn('Could not parse state, defaulting to Google');
        }
        
        const currentUrl = new URL(window.location.href);
        const redirectUri = `${currentUrl.origin}/calendar/callback`;
        
        console.log("Using redirect URI:", redirectUri);
        console.log("Detected provider:", provider);
        
        // Determine which OAuth endpoint to use
        const oauthEndpoint = provider === 'google' 
          ? 'google-calendar-oauth'
          : 'outlook-calendar-oauth';
        
        const response = await fetch(
          `https://erticlijceutpetbahff.supabase.co/functions/v1/${oauthEndpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              code,
              state,
              redirectUri
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error response from OAuth endpoint:", errorData);
          throw new Error(errorData.error || "Failed to connect calendar");
        }

        const data = await response.json();
        
        if (data.error) {
          console.error("Error from OAuth endpoint:", data.error, data.details);
          throw new Error(data.error);
        }
        
        setStatus('success');
        const providerName = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
        toast({
          title: `${providerName} Connected`,
          description: `Your ${providerName} has been connected successfully. You'll now select which calendars to sync.`,
        });
        
        // Refresh calendar tokens after successful connection
        if (currentUser?.id) {
          await refreshCalendarTokensIfNeeded(currentUser.id);
        }
        
        setTimeout(() => {
          // Always redirect to calendar page with selectCalendars=true to show calendar selection
          navigate('/calendar?selectCalendars=true');
        }, 2000);
      } catch (error) {
        console.error("Error processing calendar connection:", error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : "Failed to connect calendar");
        toast({
          variant: "destructive",
          title: "Connection Failed",
          description: error instanceof Error ? error.message : "Failed to connect calendar",
        });
        
        // Reset the processed flags on error so user can try again with a fresh auth flow
        hasProcessed.current = false;
        processedCode.current = null;
      }
    }

    // Only run if we have the required dependencies and haven't processed yet
    if (currentUser && session && !hasProcessed.current) {
      handleCallback();
    }
  }, [currentUser, session, navigate, toast]);

  return (
    <div className="container mx-auto max-w-4xl py-12">
      <div className="bg-white/60 backdrop-blur-md p-8 rounded-2xl shadow-xl">
        <div className="flex flex-col items-center justify-center">
          {status === 'processing' && (
            <>
              <div className="mb-6">
                <Calendar className="h-16 w-16 text-blue-500 animate-pulse" />
              </div>
              <h1 className="text-2xl font-semibold mb-4">Connecting Your Calendar</h1>
              <p className="text-gray-600 text-center">
                Please wait while we complete the connection process...
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="mb-6">
                <div className="rounded-full bg-green-100 p-4">
                  <Check className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold mb-4">Calendar Connected!</h1>
              <p className="text-gray-600 text-center">
                Your calendar has been successfully connected. Taking you to calendar selection...
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="mb-6">
                <div className="rounded-full bg-red-100 p-4">
                  <X className="h-12 w-12 text-red-600" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold mb-4">Connection Failed</h1>
              <p className="text-gray-600 text-center mb-4">
                {errorMessage || "There was a problem connecting your calendar."}
              </p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => navigate('/calendar')}
                  className="px-4 py-2 rounded-md bg-blue-500 text-white"
                >
                  Back to Calendar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarCallback;
